import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import WTMonogram from '../components/WTMonogram';
import { fetchGameHashPass } from '../lib/sessionCredentials';
import '../styles/login.css';

interface Session {
  pseudo: string;
  pseudoDisplay: string;
  compet: boolean;
  token?: string;
  hashPass?: string;
}

interface AuthCallbackProps {
  onLogin: (session: Session) => void;
}

/**
 * Page de callback OAuth.
 * Reçoit le token JWT et le pseudo via les query params après une
 * authentification réussie chez Google/Facebook, et crée la session.
 */
export default function AuthCallback({ onLogin }: AuthCallbackProps) {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;

    const token = searchParams.get('token');
    const pseudo = searchParams.get('pseudo');
    const linked = searchParams.get('linked');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        google_failed: 'Échec de la connexion Google',
        apple_failed: 'Échec de la connexion Apple',
        facebook_failed: 'Échec de la connexion Facebook',
        oauth_failed: 'Échec de l\'authentification',
      };
      setError(errorMessages[errorParam] || 'Une erreur est survenue');
      return;
    }

    if (!token || !pseudo) {
      setError('Données d\'authentification manquantes');
      return;
    }

    processedRef.current = true;

    (async () => {
      if (linked) {
        console.log('[OAuth] Compte lié avec succès');
      }

      const hashPass = await fetchGameHashPass(token);

      onLogin({
        pseudo,
        pseudoDisplay: pseudo,
        compet: false,
        token,
        hashPass: hashPass || undefined,
      });
    })();
  }, [searchParams, onLogin]);

  if (error) {
    return (
      <div className="login-page">
        <div className="login-bg" />
        <div className="login-card">
          <div className="login-header">
            <WTMonogram />
            <h1 className="login-title">Webtarot</h1>
            <p className="login-tagline">Authentification</p>
          </div>
          <div className="oauth-callback-content">
            <div className="login-error">{error}</div>
            <a href="/" className="oauth-back-link">Retour à la page de connexion</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-bg" />
      <div className="login-card">
        <div className="login-header">
          <WTMonogram />
          <h1 className="login-title">Webtarot</h1>
          <p className="login-tagline">Connexion en cours...</p>
        </div>
        <div className="oauth-callback-content">
          <div className="loading-spinner" />
          <p className="oauth-status-text">Authentification en cours...</p>
        </div>
      </div>
    </div>
  );
}
